import { useId, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Button, Field, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { MAX_NOTE_BODY, type DraftNoteOutcome } from '@/components/documents/draft-note';

/*
 * The furniture a QUOTE form and an INVOICE form share.
 *
 * Every component here shipped TWICE, byte for byte, in `QuoteFormModal` and
 * `InvoiceFormModal` — the disclosure under two different names, the icons
 * under one different one. The vocabulary those forms share (the line-amount
 * enum, the totals keys, the two patterns) sits beside this file in
 * `document-form.ts`; it is split off only because a module exporting both
 * components and constants breaks React Fast Refresh.
 */

/**
 * A disclosure inside a document form — the totals, and the Xero document
 * fields.
 *
 * Built from a button and a `hidden` region rather than `<details>`, so a
 * failed save can force it open without fighting the element's own state, and
 * so the collapsed content stays mounted: typing in a section then closing it
 * must not throw the input away. Pass `onToggle: undefined` for the read-only
 * variant, which has nothing to hide.
 *
 * This shipped twice under two names — `FormSection` on the quote form,
 * `DisclosurePanel` on the invoice form — differing in nothing but the name and
 * the suffix on an internal `useId`.
 */
export function FormSection({
  title,
  summary,
  expanded,
  onToggle,
  errorCount,
  children,
}: {
  title: string;
  summary: string;
  expanded: boolean;
  onToggle: (() => void) | undefined;
  errorCount: number;
  children: ReactNode;
}) {
  const uid = useId();
  const panelId = `${uid}-section`;
  const heading = (
    <>
      <span className="text-sm font-medium text-fg">{title}</span>
      <span className="hidden text-xs text-fg-muted sm:inline">{summary}</span>
      {errorCount > 0 && (
        <span className="ml-auto text-2xs font-semibold text-danger">
          {errorCount === 1 ? '1 problem' : `${errorCount} problems`}
        </span>
      )}
    </>
  );

  return (
    <section className="rounded-lg border border-border">
      <h3>
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={panelId}
            className={cx(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              'hover:bg-surface-hover',
              expanded && 'rounded-b-none',
            )}
          >
            <ChevronIcon open={expanded} />
            {heading}
          </button>
        ) : (
          <div className="flex w-full items-center gap-2 px-3 py-2.5">{heading}</div>
        )}
      </h3>
      <div id={panelId} hidden={!expanded} className="border-t border-border px-3 py-3">
        {children}
      </div>
    </section>
  );
}

/** The disclosure chevron. Colour comes from a token, so no `dark:` variant. */
export function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      {open ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  );
}

/**
 * The warning triangle that leads an alert block.
 *
 * `currentColor`, so the block that holds it decides whether it reads as a
 * warning or as a danger — never a colour of its own.
 *
 * This used to name a hand-copied third instance of the same SVG in the quote
 * acceptance dialog, and say it should adopt this one. That file was deleted on
 * 2026-08-29 (backend release `20260829011913-5fbab06` — accepting a quote now
 * moves the stage and nothing more), so the copy went with it and the note is
 * spent.
 */
export function AlertIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="mt-px h-4 w-4 shrink-0"
    >
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  );
}

/*
 * The rule and the spacing that separate the note from the totals above it, kept
 * HERE rather than at the two call sites.
 *
 * ⚠️ Not cosmetic: the component below returns `null` on the ordinary path — a
 * create where nobody typed anything — and a wrapper owned by the form would
 * then paint a hairline rule and 16px of padding under an empty region. The band
 * and the thing that fills it have to disappear together.
 */
const BAND = 'grid gap-2 border-t border-border pt-4';

/**
 * The note somebody types while CREATING a quote or an invoice, and the honest
 * report of what became of it.
 *
 * One component for both forms, deliberately. Everything difficult here is
 * WORDING about a two-phase save — "the document exists, the note did not" — and
 * two copies of that sentence would agree the day they were written and drift on
 * the first correction. The wire half is `draft-note.ts` beside this file.
 *
 * ## What it renders, and when
 *
 * - **Before the create** — a plain box. Nothing explains it: the label says
 *   what it is, and the no-helper-text rule stands.
 * - **After the create, with nothing typed** — nothing at all. `outcome: null`
 *   means no note was ATTEMPTED, which is a different fact from one that failed
 *   and must never be reported as one.
 * - **After the create, saved** — one line saying so.
 * - **After the create, refused** — the box again, still holding what was typed,
 *   with a refusal above it and a button that retries against the document that
 *   now exists.
 *
 * ⚠️ **The refusal opens by restating that the document was created**, on the
 * same reasoning as `CreatePushNote`: a red block under a green one is read as a
 * correction of it, and the correction a reader supplies for themselves is "so
 * it did not save after all". They then close the form and raise a second
 * document. Nothing here offers to undo anything, because nothing can.
 *
 * ⚠️ **The retry is not a rollback and must never be worded as one.** It is a
 * plain `POST /notes` against a real id, which is exactly what the recovery
 * would be from the record's own Tasks & notes drawer — offered here because the
 * text is already on screen and retyping it is the thing a person should not
 * have to do.
 */
export function DraftNoteSection({
  documentNoun,
  value,
  onChange,
  disabledReason,
  outcome,
  created,
  onRetry,
  retrying,
}: {
  /** The word this form's document goes by, so one sentence serves both. */
  documentNoun: 'quote' | 'invoice';
  value: string;
  onChange: (next: string) => void;
  /**
   * Null when the reader may add notes, otherwise why they may not.
   *
   * ⚠️ The box is DISABLED with the reason beside it rather than removed. A
   * bare `disabled` makes a permission denial and an ordinary empty control
   * identical, and an absent one cannot be told from a broken form — the same
   * call `EnvelopePanel` makes for Check status. A reason for a disabled
   * control is a named exception to the no-helper-text rule.
   */
  disabledReason: string | null;
  /** What the create's note write did, or null when none was attempted. */
  outcome: DraftNoteOutcome | null;
  /** True once THIS form has created the document — see the render table above. */
  created: boolean;
  onRetry: () => void;
  retrying: boolean;
}) {
  if (created && outcome?.kind === 'saved') {
    return (
      <div className={BAND}>
        <Notice tone="success" role="status" className="break-words">
          Note saved against this {documentNoun}.
        </Notice>
      </div>
    );
  }

  /*
   * Nothing was typed and nothing was attempted, so there is nothing to report
   * and nothing left to offer.
   *
   * ⚠️ The EMPTY test is part of it, not `outcome === null` alone. A create can
   * reach its failure path with a note still unwritten and no outcome recorded
   * — the text is then held nowhere but this box, and hiding it on the strength
   * of a missing outcome would destroy it silently. Typed text survives; only a
   * genuinely empty box disappears.
   */
  if (created && outcome === null && value.trim() === '') return null;

  const refusal = created && outcome?.kind === 'failed' ? outcome.message : null;

  return (
    <div className={BAND}>
      {refusal !== null && (
        <Notice
          tone="danger"
          role="alert"
          className="break-words"
          title={<>The {documentNoun} was created. This note was not saved.</>}
        >
          {refusal} Your text is below — try again.
        </Notice>
      )}

      <Field
        label="Internal note"
        /*
         * Two named exceptions only: why the control is disabled, and a refusal.
         * A refused note keeps its message in the block above — repeating it
         * here would put one sentence in two places — so this slot carries the
         * permission reason alone.
         */
        hint={disabledReason ?? undefined}
      >
        {({ id, 'aria-describedby': describedBy }) => (
          <Textarea
            id={id}
            rows={3}
            value={value}
            maxLength={MAX_NOTE_BODY}
            disabled={disabledReason !== null || retrying}
            invalid={refusal !== null}
            aria-describedby={describedBy}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </Field>

      {/*
        Only once the document exists. Before the create there is no button,
        because the form's own Save is what writes this — a second button would
        read as a separate action and invite somebody to press it first, against
        a document that does not exist yet.
      */}
      {created && (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onRetry}
            loading={retrying}
            disabled={retrying || value.trim() === ''}
          >
            Save note
          </Button>
        </div>
      )}
    </div>
  );
}
